import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import styles from './Dashboard.module.css';
import config from '../config';
import { getToken } from '../utils/api';
import { triggerWalkMeThrough } from '../utils/walkMeThroughBridge';
import { getFirstName, getFirstNameFromStorage } from '../utils/helper';
import { identifyUser, clearIdentifiedSession, capture, LOGGED_OUT_EVENT } from '../analytics/posthog';

// Constants
const MOBILE_BREAKPOINT = 768;
const MAX_RETRIES = 5;
const RETRY_INTERVAL = 300;

// Path to menu name mapping for active menu detection
const PATH_TO_MENU_MAP = {
  '/copilot': 'Copilot',
  '/forecast': 'Forecast',
  '/tracks/your-quick-richuels': 'Your Quick Richuels',
  '/tracks/your-weekly-wins': 'Your Weekly Wins',
  '/tracks/your-monthly-wins': 'Your Monthly Wins',
  '/tracks/your-progress': 'Your Progress',
  '/meet-an-expert': 'Meet an Expert',
  '/schedule-cfp-session': 'Meet an Expert',
  '/assumptions': 'Assumptions',
  '/dashboard/career-shift-scenario-map': 'Scenario Map',
  '/explorer': 'Explorer',
  '/settings': 'Settings',
  '/community': 'Community'
};

const USER_AVATAR_SRC = `${process.env.PUBLIC_URL || ''}/user.png`;

const NAVIGATION_ITEMS = [
  {
    type: 'group',
    name: 'Next Best Money Moves',
    icon: 'next-best-money-moves',
    children: [
      { name: 'Your Quick Richuels', icon: 'your-quick-richuels', route: '/dashboard/tracks/your-quick-richuels' },
      { name: 'Your Weekly Wins', icon: 'your-weekly-wins', route: '/dashboard/tracks/your-weekly-wins' },
      { name: 'Your Monthly Wins', icon: 'your-monthly-wins', route: '/dashboard/tracks/your-monthly-wins' },
      { name: 'Your Progress', icon: 'your-progress', route: '/dashboard/tracks/your-progress' }
    ]
  },
  { name: 'Copilot', icon: 'copilot', route: '/dashboard/copilot' },
  { name: 'Forecast', icon: 'forecast', route: '/dashboard/forecast' },
  { name: 'Explorer', icon: 'explorer', route: '/dashboard/explorer' },
  { name: 'Scenario Map', icon: 'scenario-map', route: '/dashboard/career-shift-scenario-map' },
  { name: 'Assumptions', icon: 'assumptions', route: '/dashboard/assumptions' },
  { name: 'Meet an Expert', icon: 'support', route: '/dashboard/meet-an-expert' },
  { name: 'Community', icon: 'community', route: '/dashboard/community', upcoming: true },
  { name: 'Settings', icon: 'settings', route: '/dashboard/settings' }
];

// Custom hook for loading userName from localStorage with retry logic
const useUserName = () => {
  const [userName, setUserName] = useState('User');
  const [userNameLoaded, setUserNameLoaded] = useState(false);

  useEffect(() => {
    const loadUserName = () => {
      // Check immediately
      let name = localStorage.getItem(config.STORAGE_KEYS.NAME);
      if (name) {
        setUserName(getFirstName(name) || 'User');
        setUserNameLoaded(true);
        return;
      }

      // If not found, retry multiple times
      let retryCount = 0;

      const retryLoad = () => {
        retryCount++;
        name = localStorage.getItem(config.STORAGE_KEYS.NAME);
        if (name) {
          setUserName(getFirstName(name) || 'User');
          setUserNameLoaded(true);
          return;
        }

        if (retryCount < MAX_RETRIES) {
          setTimeout(retryLoad, RETRY_INTERVAL);
        } else {
          // After all retries, mark as loaded even if name not found
          setUserNameLoaded(true);
        }
      };

      setTimeout(retryLoad, RETRY_INTERVAL);
    };

    loadUserName();
  }, []);

  return { userName, userNameLoaded };
};

// Custom hook for responsive mobile detection
const useMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

// Custom hook for active menu detection
const useActiveMenu = (location) => {
  const [activeMenu, setActiveMenu] = useState('Forecast');

  useEffect(() => {
    const path = location.pathname;
    let menuName = 'Forecast'; // Default

    // Find matching menu by checking path segments
    for (const [pathSegment, menu] of Object.entries(PATH_TO_MENU_MAP)) {
      if (path.includes(pathSegment)) {
        menuName = menu;
        break;
      }
    }

    setActiveMenu(menuName);
  }, [location.pathname]);

  return activeMenu;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const posthog = usePostHog();
  const { userName, userNameLoaded } = useUserName();
  const isMobile = useMobile();
  const activeMenu = useActiveMenu(location);

  // State
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Check authentication token on mount
  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  // Identify user for PostHog when we have token (e.g. returning visits). Deduped per session in identifyUser.
  useEffect(() => {
    if (!posthog || !userNameLoaded) return;
    const token = getToken();
    if (token) {
      const name = localStorage.getItem(config.STORAGE_KEYS.NAME);
      identifyUser(posthog, token, { name: name || userName });
    }
  }, [posthog, userNameLoaded, userName]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Close profile menu on outside click; dismiss overlays/menu with Escape
  useEffect(() => {
    if (!profileMenuOpen && !mobileSidebarOpen) return;

    const handlePointerDown = (event) => {
      if (
        profileMenuOpen &&
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target)
      ) {
        setProfileMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      setProfileMenuOpen(false);
      setMobileSidebarOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [profileMenuOpen, mobileSidebarOpen]);

  const handleCollapseClick = useCallback(() => {
    if (isMobile) {
      setMobileSidebarOpen(prev => !prev);
    } else {
      setSidebarCollapsed(prev => !prev);
    }
  }, [isMobile]);

  const handleMenuClick = useCallback((menuName, route) => {
    if (route) navigate(route);
    if (isMobile) setMobileSidebarOpen(false);
  }, [navigate, isMobile]);

  const isRouteActive = useCallback((route, exact = false) => {
    if (!route) return false;
    if (exact) return location.pathname === route;
    return location.pathname === route || location.pathname.startsWith(`${route}/`);
  }, [location.pathname]);

  const isItemActive = useCallback((name, route) => {
    if (activeMenu === name) return true;
    if (route && isRouteActive(route)) return true;
    return false;
  }, [activeMenu, isRouteActive]);

  const handleOverlayClick = useCallback(() => {
    setMobileSidebarOpen(false);
  }, []);

  const forecastPath = location.pathname.replace(/\/+$/, '') || '/';
  const isForecastPage = forecastPath === '/dashboard' || forecastPath === '/dashboard/forecast';

  const handleTakeTour = useCallback(() => {
    triggerWalkMeThrough();
  }, []);

  const displayFirstName = useMemo(() => {
    const fromStorage = getFirstNameFromStorage();
    if (fromStorage) return fromStorage;
    return getFirstName(userName) || 'User';
  }, [userName]);

  const handleHeaderMenuToggle = useCallback(() => {
    setProfileMenuOpen(prev => !prev);
  }, []);

  const handleHeaderMenuSelect = useCallback((item) => {
    setProfileMenuOpen(false);
    if (item === 'settings') {
      navigate('/dashboard/settings');
    } else if (item === 'logout') {
      capture(posthog, LOGGED_OUT_EVENT);
      clearIdentifiedSession();
      localStorage.clear();
      window.location.href = '/login';
    }
  }, [posthog, navigate]);

  return (
    <div className={`${styles.dashboard} ${sidebarCollapsed ? styles.sidebarCollapsed : ''}`}>
      {/* Mobile Overlay */}
      {mobileSidebarOpen && (
        <div
          className={`${styles.sidebarOverlay} ${mobileSidebarOpen ? styles.active : ''}`}
          onClick={handleOverlayClick}
          aria-hidden="true"
        />
      )}

      {/* Left Sidebar */}
      <aside
        id="dashboard-sidebar"
        className={`${styles.sidebar} ${sidebarCollapsed ? styles.collapsed : ''} ${mobileSidebarOpen ? styles.open : ''}`}
        aria-label="Main navigation"
      >
        <div className={styles.sidebarHeader}>
          <h2 className={styles.brandName}>Richuel</h2>
          <button
            type="button"
            className={styles.collapseButton}
            onClick={handleCollapseClick}
            aria-label={isMobile ? 'Close menu' : 'Collapse sidebar'}
          >
            <div className={styles.collapseIcon}>
              <div className={styles.sidebarBar} aria-hidden />
              <div className={styles.arrowIcon} aria-hidden>
                ←
              </div>
            </div>
          </button>
        </div>

        <nav className={styles.navigation} aria-label="Dashboard navigation">
          {NAVIGATION_ITEMS.map((item) => {
            if (item.type === 'group') {
              const groupActive = item.children.some((child) => isRouteActive(child.route));
              return (
                <div key={item.name} className={styles.navGroup}>
                  <button
                    type="button"
                    className={`${styles.navItem} ${styles.navGroupHeader} ${groupActive ? styles.active : ''}`}
                    onClick={() => handleMenuClick(item.children[0].name, item.children[0].route)}
                    aria-current={groupActive ? 'true' : undefined}
                  >
                    <span className={`${styles.navIcon} ${styles[item.icon]}`} aria-hidden />
                    {!sidebarCollapsed && <span className={styles.navText}>{item.name}</span>}
                  </button>
                  {!sidebarCollapsed && (
                    <div className={styles.subMenu}>
                      {item.children.map((child) => {
                        const childActive = isRouteActive(child.route);
                        return (
                          <button
                            type="button"
                            key={child.name}
                            className={`${styles.subNavItem} ${childActive ? styles.active : ''}`}
                            onClick={() => handleMenuClick(child.name, child.route)}
                            aria-current={childActive ? 'page' : undefined}
                          >
                            <span className={`${styles.navIcon} ${styles[child.icon]}`} aria-hidden />
                            <span className={styles.navText}>{child.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const itemActive = isItemActive(item.name, item.route);
            return (
              <button
                type="button"
                key={item.name}
                className={`${styles.navItem} ${itemActive ? styles.active : ''}`}
                onClick={() => handleMenuClick(item.name, item.route)}
                aria-current={itemActive ? 'page' : undefined}
              >
                <span className={`${styles.navIcon} ${styles[item.icon]}`} aria-hidden />
                {!sidebarCollapsed && <span className={styles.navText}>{item.name}</span>}
                {item.upcoming && !sidebarCollapsed && (
                  <span className={styles.upcomingTag}>Upcoming</span>
                )}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Main Content Area - This will render the nested routes */}
      <main className={styles.mainContent} id="dashboard-main" aria-label="Dashboard content">
        {/* Global Header for all dashboard pages */}
        <header className={`${styles.headerBar} ${styles.shellHeader}`} role="banner">
          <div className={styles.headerLead}>
            <p className={styles.headerTitle}>Financial Wellness Hub</p>
          </div>
          <div className={styles.headerRight}>
            {isForecastPage && (
              <button
                type="button"
                className={styles.takeTourButton}
                onClick={handleTakeTour}
              >
                Take a Tour
              </button>
            )}
            <div className={styles.userSection} ref={userMenuRef}>
              <button
                type="button"
                className={styles.userDropdown}
                onClick={handleHeaderMenuToggle}
                aria-haspopup="true"
                aria-expanded={profileMenuOpen}
                aria-label={`User menu for ${displayFirstName}`}
              >
                <span className={styles.userAvatar} aria-hidden="true">
                  <img
                    src={USER_AVATAR_SRC}
                    alt=""
                    className={styles.userAvatarImage}
                    draggable="false"
                  />
                </span>
              </button>
              {profileMenuOpen && (
                <div className={styles.dropdownMenu} role="menu" aria-label="User menu">
                  <div className={styles.dropdownHeader}>
                    <span className={styles.dropdownAvatar} aria-hidden="true">
                      <img src={USER_AVATAR_SRC} alt="" draggable="false" />
                    </span>
                    <span className={styles.dropdownUserName}>{displayFirstName}</span>
                  </div>
                  <button
                    type="button"
                    className={styles.dropdownItem}
                    role="menuitem"
                    onClick={() => handleHeaderMenuSelect('settings')}
                  >
                    <span className={`${styles.navIcon} ${styles.settings}`} aria-hidden="true" />
                    <span>Settings</span>
                  </button>
                  <button
                    type="button"
                    className={`${styles.dropdownItem} ${styles.dropdownItemLogout}`}
                    role="menuitem"
                    onClick={() => handleHeaderMenuSelect('logout')}
                  >
                    <span className={`${styles.navIcon} ${styles['logout']}`} aria-hidden="true" />
                    <span>Log out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        {/* Mobile Menu Button - Only show on mobile when sidebar is closed */}
        {!mobileSidebarOpen && (
          <button
            type="button"
            className={`${styles.collapseButton} ${styles.mobileMenuButton}`}
            onClick={() => setMobileSidebarOpen(true)}
            aria-label="Open menu"
            aria-expanded={mobileSidebarOpen}
            aria-controls="dashboard-sidebar"
          >
            <div className={styles.collapseIcon}>
              <span className={styles.sidebarBar} aria-hidden />
              <span className={styles.sidebarBar} aria-hidden />
              <span className={styles.sidebarBar} aria-hidden />
            </div>
          </button>
        )}
        <div className={styles.outletWrap}>
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Dashboard;